'use client';

import React, { useMemo } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AddIcon, RemoveIcon } from '@/shared/ui/display/EmojiIcon';
import { movingItemEditorColumnCardSx } from '@/shared/theme/movingItemEditGlass';
import type {
  AttrDef,
  AttrDefType,
  AttributeCondition,
  ConditionEffect,
  ConditionRule,
} from '../model/moving-item.types';
import { TYPE_DISPLAY } from '../model/moving-item.types';
import { ConditionRuleList } from './ConditionRuleList';
import { EnumOptionBlock } from './EnumOptionBlock';

const LG_SECTION_LABEL = {
  fontSize: '0.68rem',
  fontWeight: 600,
  color: 'text.secondary',
} as const;

export interface AttrDefEditorProps {
  attributes: AttrDef[];
  /** 속성에 종속되지 않는 스펙 공통 조건 */
  specConditions?: AttributeCondition[];
  onUpdateAttr: (index: number, updates: Partial<AttrDef>) => void;
  onAddAttr: () => void;
  onRemoveAttr: (index: number) => void;
  onUpdateEnumEntry: (
    attrIndex: number,
    entryIndex: number,
    field: 'value' | 'displayName',
    value: string
  ) => void;
  onAddEnumEntry: (attrIndex: number) => void;
  onRemoveEnumEntry: (attrIndex: number, entryIndex: number) => void;
  onAddCondition?: (attrIndex: number) => void;
  onRemoveCondition?: (attrIndex: number, condIndex: number) => void;
  onUpdateConditionRule?: (
    attrIndex: number,
    condIndex: number,
    updates: Partial<ConditionRule>
  ) => void;
  onUpdateConditionEffect?: (
    attrIndex: number,
    condIndex: number,
    updates: Partial<ConditionEffect>
  ) => void;
  onMoveCondition?: (fromAttrIndex: number, condIndex: number, toAttrIndex: number) => void;
}

export function AttrDefEditor({
  attributes,
  specConditions = [],
  onUpdateAttr,
  onAddAttr,
  onRemoveAttr,
  onUpdateEnumEntry,
  onAddEnumEntry,
  onRemoveEnumEntry,
  onAddCondition,
  onRemoveCondition,
  onUpdateConditionRule,
  onUpdateConditionEffect,
  onMoveCondition,
}: AttrDefEditorProps) {
  const theme = useTheme();
  const columnCardSx = movingItemEditorColumnCardSx(theme);

  const totalRules = useMemo(
    () => specConditions.length + attributes.reduce((s, a) => s + (a.conditions?.length ?? 0), 0),
    [attributes, specConditions]
  );

  const hasRulesCallbacks =
    onAddCondition && onRemoveCondition && onUpdateConditionRule && onUpdateConditionEffect;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) minmax(0,1fr)' },
        gap: 1.25,
        alignItems: 'stretch',
      }}
    >
      <Box
        sx={{
          ...columnCardSx,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          flex: 1,
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: LG_SECTION_LABEL.fontSize,
            fontWeight: LG_SECTION_LABEL.fontWeight,
            color: LG_SECTION_LABEL.color,
          }}
        >
          속성 정의
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
          {attributes.map((attr, attrIndex) => (
            <Box
              key={attrIndex}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                pb: 0.75,
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-of-type': { borderBottom: 0, pb: 0 },
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'minmax(0, 1fr) minmax(0, 1fr) minmax(100px, 140px) auto',
                  },
                  gap: 1,
                  alignItems: 'center',
                  minWidth: 0,
                }}
              >
                <TextField
                  variant="filled"
                  label="키"
                  size="small"
                  value={attr.key}
                  onChange={(e) => onUpdateAttr(attrIndex, { key: e.target.value })}
                  sx={{ minWidth: 0, gridColumn: { xs: '1', sm: 'auto' } }}
                />
                <TextField
                  variant="filled"
                  label="표시명"
                  size="small"
                  value={attr.name ?? ''}
                  onChange={(e) => onUpdateAttr(attrIndex, { name: e.target.value })}
                  sx={{ minWidth: 0, gridColumn: { xs: '1', sm: 'auto' } }}
                />
                <FormControl
                  variant="filled"
                  size="small"
                  sx={{
                    minWidth: 0,
                    width: { xs: '100%', sm: 'auto' },
                    gridColumn: { xs: '1', sm: 'auto' },
                  }}
                >
                  <InputLabel>타입</InputLabel>
                  <Select
                    label="타입"
                    value={attr.type}
                    onChange={(e) =>
                      onUpdateAttr(attrIndex, {
                        type: e.target.value as AttrDefType,
                        ...(e.target.value === 'ENUM' && !attr.values ? { values: [''] } : {}),
                      })
                    }
                  >
                    <MenuItem value="STRING">{TYPE_DISPLAY.STRING}</MenuItem>
                    <MenuItem value="NUMBER">{TYPE_DISPLAY.NUMBER}</MenuItem>
                    <MenuItem value="BOOLEAN">{TYPE_DISPLAY.BOOLEAN}</MenuItem>
                    <MenuItem value="ENUM">{TYPE_DISPLAY.ENUM}</MenuItem>
                  </Select>
                </FormControl>
                <IconButton
                  size="small"
                  onClick={() => onRemoveAttr(attrIndex)}
                  aria-label="속성 삭제"
                  sx={{
                    gridColumn: { xs: '1', sm: 'auto' },
                    justifySelf: { xs: 'end', sm: 'auto' },
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Box>
              {attr.type === 'ENUM' && (
                <EnumOptionBlock
                  attrIndex={attrIndex}
                  values={attr.values ?? []}
                  enumDisplayNames={attr.enumDisplayNames}
                  onUpdateEntry={onUpdateEnumEntry}
                  onAddEntry={onAddEnumEntry}
                  onRemoveEntry={onRemoveEnumEntry}
                />
              )}
            </Box>
          ))}
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddAttr}
          sx={{ alignSelf: 'flex-start' }}
        >
          속성 추가
        </Button>
      </Box>

      {hasRulesCallbacks && (
        <Box
          sx={{
            ...columnCardSx,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            flex: 1,
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: LG_SECTION_LABEL.fontSize,
              fontWeight: LG_SECTION_LABEL.fontWeight,
              color: LG_SECTION_LABEL.color,
            }}
          >
            {totalRules > 0 ? `규칙·조건 (${totalRules})` : '규칙·조건'}
          </Typography>
          <ConditionRuleList
            attributes={attributes}
            specConditions={specConditions}
            onAddCondition={onAddCondition}
            onRemoveCondition={onRemoveCondition}
            onUpdateConditionRule={onUpdateConditionRule}
            onUpdateConditionEffect={onUpdateConditionEffect}
            onMoveCondition={onMoveCondition}
          />
        </Box>
      )}
    </Box>
  );
}
